import React, { useContext } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import { red, yellow } from "@mui/material/colors";
import greenGem from "../@shared/assets/green_gem.webp";
import shieldIcon from "../@shared/assets/icons/sheild.png";
import swordIcon from "../@shared/assets/icons/swords_icon2.png";
import healthIcon from "../@shared/assets/icons/heart.png";
import crosshairIcon from "../@shared/assets/icons/cross-hair.png";
import timeIcon from "../@shared/assets/icons/time_icon.png";
import targetIcon from "../@shared/assets/icons/target.png";
import selfIcon from "../@shared/assets/icons/self.png";
import bothIcon from "../@shared/assets/icons/both.png";

import { MainContext } from "../App";

// import zIndex from "@mui/material/styles/zIndex";

const InfinityCard = ({
  name,
  description,
  effect,
  areaAffected,
  turnsActive,
  max,
  finite,
  specialty,
  craftNeeded,
  craftAmount,
  availableForPurchase,
  season,
  imageUrl,
  bgColor1,
  bgColor2,
  heightPer = 60,
  amountOwned,
  showAmountOwned = false,
  borderPx = 8,
  onClick,
  containerHeight,
}) => {
  const screenHeight = window.innerHeight;
  const aspectRatio = 2.5 / 3.5;
  const { Theme } = useContext(MainContext);

  const cardHeight = containerHeight
    ? (containerHeight * heightPer) / 100
    : (screenHeight * heightPer) / 100;
  const cardWidth = cardHeight * aspectRatio;

  const bannerNameSize = cardHeight * 0.04;
  const sectionLabelSize = cardHeight * 0.038;
  const sectionContentSize = cardHeight * 0.028;
  const cardBorderRadius = cardHeight * 0.01;
  const cardInnerPanelRadius = cardHeight * 0.013;
  const cardBorderWidth = cardHeight * 0.015;
  const cardBackgroundPadding = cardHeight * 0.0025;
  const cardBorderRadiusInner = cardHeight * 0.00875;
  const bottomContentPadding1 = cardHeight * 0.025;
  const bottomContentPadding2 = cardHeight * 0.035;
  const infinityImageTopMargin = cardHeight * 0.13;
  const cotainerPadding = cardHeight * 0.03;
  const maxTextSize = cardHeight * 0.05;

  const buffMatch = effect?.match(/(\d+)/) ?? 0;
  const buffValue = buffMatch ? parseInt(buffMatch[1], 10) : null;

  let typeIconSrc = null;
  if (areaAffected?.includes("Defense")) typeIconSrc = shieldIcon;
  else if (areaAffected?.includes("Attack")) typeIconSrc = swordIcon;
  else if (areaAffected?.includes("Health")) typeIconSrc = healthIcon;
  else if (areaAffected?.includes("Accuracy")) typeIconSrc = crosshairIcon;

  // Only parse turnsActive if it's not "instant"
  const isInstant = turnsActive?.toLowerCase() === "instant";
  let turnCount = null;

  if (!isInstant && turnsActive) {
    const match = turnsActive.match(/(\d+)/);
    turnCount = match ? parseInt(match[1], 10) : null;
  }

  const compressEffectText = (effect = "", areaAffected = []) => {
    const hasTurnLoss = /turn/.test(areaAffected.join().toLowerCase());
    const knownStats = [
      "attack",
      "defense",
      "accuracy",
      "health",
      "speed",
      "evasion",
      "power",
    ];

    const sentences = effect
      .split(".")
      .map((s) => s.trim())
      .filter(Boolean);

    return sentences
      .map((sentence) => {
        const combined = sentence.toLowerCase();
        const numberMatch = sentence.match(/([+-]?\d+)/);
        const num = numberMatch ? numberMatch[1] : null;

        const isAffliction =
          /reduce|decrease|weaken|lower|afflict|damage|poison|lose|remove|takes/.test(
            combined
          );
        const isAid = /restore|increase|heal|boost|raise|enhance|aid/.test(
          combined
        );

        // Replace "for X turns", "over X turns", "across X turns" → "xX"
        let durationMatch = sentence.match(
          /(?:for|over|across)\s+(\d+)\s+turns?/i
        );
        let duration = durationMatch ? `x${durationMatch[1]}` : "";

        // Handle turn loss (e.g., "Target loses 1 turn")
        if (
          hasTurnLoss &&
          combined.includes("turn") &&
          combined.includes("lose")
        ) {
          return `-${num} Turn.`;
        }

        // Try to find the stat mentioned in the sentence
        let stat = knownStats.find((stat) => combined.includes(stat)) || "";

        // Capitalize stat
        stat = capitalize(stat);

        // Final compressed output
        if (isAid) return `+${num} ${stat} ${duration}`.trim() + ".";
        if (isAffliction) return `-${num} ${stat} ${duration}`.trim() + ".";

        return sentence + ".";
      })
      .join(" ");
  };

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const calculateCardStars = () => {
    const parsedMax = Number(max);
    if (parsedMax === 1) return 5;
    if (parsedMax === 2) return 4;

    let stars = 1;

    const effectNumbers = effect?.match(/([-+]?\d+)/g)?.map(Number) || [];
    const totalAbsolute = effectNumbers.reduce(
      (sum, n) => sum + Math.abs(n),
      0
    );

    if (totalAbsolute >= 4) stars += 1;
    if (totalAbsolute >= 5) stars += 1;
    if (finite !== "FALSE" && finite !== false) stars += 1;
    if (specialty === true) stars += 1;

    return Math.min(stars, 5);
  };

  const resolvedDescriptionText =
    effect?.length + description?.length < 130
      ? `${description} - ${effect}`
      : `${description} ${compressEffectText(effect, areaAffected)}`;
  const descriptionFontSize =
    resolvedDescriptionText.length > 99
      ? sectionContentSize * 0.85
      : sectionContentSize;

  return (
    <Box
      height={`${heightPer}%`}
      width="auto"
      sx={{ aspectRatio: "2.5 / 3.5", position: "relative" }}
      // style={{ height: cardHeight, width: cardWidth }}
      onClick={onClick}
    >
      <Card
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          boxShadow:'none',
          flexDirection: "column",
          backgroundColor: specialty
            ? Theme.palette.tirciary.dark
            : finite === "FALSE" || finite === false
            ? yellow[700]
            : "#da1d4e",
          borderRadius: cardBorderRadius,
          overflow: "visible",
          position: "relative",
          padding: cardBackgroundPadding,
          containerType: "inline-size",
        }}
      >
        {showAmountOwned && (
          <Box
            style={{
              backgroundColor: "#00000090",
              padding: "2% 4%",
              borderRadius: 20,
              position: "absolute",
              top: "-2%",
              right: "0%",
              zIndex: 10,
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontSize: maxTextSize,
                fontWeight: "bold",
                color: "#fff",
              }}
            >
              {amountOwned}
            </Typography>
          </Box>
        )}

        <Box
          style={{
            position: "absolute",
            zIndex: 0,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: "94%",
            width: "93%",
            backgroundImage: `linear-gradient(to bottom, ${bgColor1}, ${bgColor2})`,
            borderRadius: cardInnerPanelRadius,
          }}
        />
        <CardContent
          //  sx={{ flexGrow: 1, padding: 0 }} style={{ paddingBottom: 0 }}
          sx={{
            flexGrow: 1,
            padding: 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Grid
            container
            item
            xs={12}
            sx={{
              position: "relative",
              height: "60%",
              //   overflow: "hidden",
              padding: 2,
              containerType: "inline-size",
            }}
          >
            <img
              src={imageUrl}
              alt={""}
              style={{
                height: "70%",
                width: "auto",
                position: "absolute",
                top: "56%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "4%",
                left: "9%",
                width: "80%",
                height: "14%",
                backgroundColor: "rgba(255,255,255,0.8)",
                transform: "skewX(-20deg)",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                display: "flex",
                alignItems: "center",
                paddingLeft: "5%",
                zIndex: 2,
              }}
            >
              <Typography
                style={{ fontSize: name.length > 20 ? bannerNameSize * 0.8 : bannerNameSize, fontWeight: "bold", color: "#000" }}
              >
                {name}
              </Typography>
            </Box>
            <img
              src={greenGem}
              alt=""
              style={{
                width: "14%",
                height: "auto",
                position: "absolute",
                top: "-1.5%",
                left: "-1%",
                zIndex: 2,
              }}
            />
            {typeIconSrc && (
              <Box
                sx={{
                  position: "absolute",
                  top: "25%", // tweak these so it sits right under the greenGem
                  left: "-2%",
                  width: "18%", // same relative size as your gem
                  height: "auto",
                  zIndex: 2,
                }}
              >
                {/* the card‐specific icon */}
                <img
                  src={typeIconSrc}
                  alt=""
                  style={{ width: "100%", height: "auto" }}
                />
                {/* the buff number, centered over the icon */}
                {buffValue !== null && (
                  <Typography
                    sx={{
                      position: "absolute",
                      top: { xs: "43%", md: "48%" },
                      left: "48%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "12cqw",
                      fontWeight: "bold",
                      color: "#ffffff",
                      // textShadow: "0 0 10px rgba(0,0,0,0.8)",
                      textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                      zIndex: 3,
                    }}
                  >
                    {buffValue}
                  </Typography>
                )}
              </Box>
            )}

            {!isInstant && timeIcon && turnCount !== null && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%", // push it further down under the type icon
                  left: "-4%",
                  width: "18%", // same size as type icons
                  height: "auto",
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={timeIcon}
                  alt=""
                  style={{ width: "100%", height: "auto" }}
                />
                <Typography
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "12cqw",
                    fontWeight: "bold",
                    color: "#fff",
                    // textShadow: "0 0 4px rgba(252, 252, 252, 0.8)",
                    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                    zIndex: 3,
                  }}
                >
                  {turnCount}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid
            container
            item
            xs={12}
            sx={{
              height: "36%",
              position: "relative",
              containerType: "inline-size",
            }}
          >
            {/* STAR SYSTYEM HERE.... */}
            <Box
              style={{
                width: "60%",
                position: "absolute",
                top: "-10%",
                right: "5%",
                height: "18%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {[...Array(5)].map((_, index) => {
                const filledStars = calculateCardStars();
                const isFilled = index >= 5 - filledStars; // Flip logic: start filling from the right

                return (
                  <img
                    key={index}
                    src={require(`../@shared/assets/icons/star_icon.png`)}
                    alt="star"
                    style={{
                      width: "auto",
                      height: "100%",
                      objectFit: "contain",
                      marginRight: 2,
                      opacity: isFilled ? 1 : 0.2,
                      zIndex: 100,
                    }}
                  />
                );
              })}
            </Box>

            {(specialty || finite === "TRUE" || finite === true) && (
              <Box
                style={{
                  width: "18%",
                  position: "absolute",
                  top: "-8%",
                  left: "5%",
                  height: "18%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10cqw",
                    fontWeight: "bold",
                    color: "#fff",
                    textShadow: `-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000`,
                  }}
                >
                  {specialty ? "S" : "F"}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                height: { xs: "100%", md: "100%" },
                backgroundColor: "#ffffff",
                zIndex: 1,
                width: "82%",
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                padding: `${bottomContentPadding1}px ${bottomContentPadding2}px`,
                containerType: "inline-size",
                // clipPath: "polygon(95% 94%, 5% 94%, 0% 0%, 100% 0%)",
                borderRadius: cardBorderRadiusInner * 0.5,
                boxShadow: [
                  // top shadow: y-offset = -8px, large blur, high opacity
                  "0px -4px 16px rgba(0,0,0,0.5)",
                  // bottom shadow: y-offset = 4px, medium blur, lower opacity
                  "0px 4px 8px rgba(0,0,0,0.2)",
                ].join(", "),
              }}
            >
              <Box
                sx={{
                  height: "fit-content",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  style={{
                    fontSize: sectionLabelSize,
                    fontWeight: "bold",
                    marginBottom: "2px",
                    color: "#000",
                  }}
                >
                  Description:
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: descriptionFontSize,
                    marginBottom: "2px",
                    color: "#000",
                  }}
                >
                  <span>{resolvedDescriptionText}</span>
                </Typography>

                <Typography
                  style={{
                    fontSize: sectionLabelSize,
                    fontWeight: "bold",
                    // marginTop: "1%",
                    color: "#000",
                  }}
                >
                  <strong>
                    Affected Area{areaAffected?.length > 1 && "s"}:{" "}
                  </strong>
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "4px",
                    flexDirection: "row",
                    flexShrink: 1,
                  }}
                >
                  {areaAffected?.map((area, idx) => {
                    let icon = null;
                    let cleanText = area;

                    if (area.includes("(target)")) {
                      icon = targetIcon;
                      cleanText = area.replace(" (target)", "");
                    } else if (area.includes("(self)")) {
                      icon = selfIcon;
                      cleanText = area.replace(" (self)", "");
                    } else if (area.includes("(both)")) {
                      icon = bothIcon;
                      cleanText = area.replace(" (both)", "");
                    }

                    return (
                      <Box
                        key={idx}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Typography
                          component="span"
                          style={{
                            fontSize: areaAffected?.length > 3 ? sectionContentSize * 0.7 : sectionContentSize,
                            marginRight: "2px",
                            color: "#000",
                          }}
                        >
                          {cleanText}
                        </Typography>
                        {icon && (
                          <img
                            src={icon}
                            alt=""
                            style={{
                              height: sectionContentSize * 1.2,
                              marginRight:
                                idx !== areaAffected.length - 1 ? "6px" : 0,
                            }}
                          />
                        )}
                        {idx !== areaAffected.length - 1 && (
                          <Typography
                            component="span"
                            style={{
                              fontSize: sectionContentSize,
                              marginRight: "6px",
                              color: "#000",
                            }}
                          >
                            ,
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                <Typography
                  style={{ fontSize: sectionContentSize, marginTop: "1%", color: "#000" }}
                >
                  <strong>Active:</strong> {turnsActive}
                </Typography>
              </Box>
            </Box>

            {showAmountOwned && (
              <Box
                style={{
                  backgroundColor: Theme.palette.primary.main,
                  padding: "2% 4%",
                  borderRadius: 50,
                  position: "absolute",
                  bottom: "-32%",
                  right: "0%",
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="body2"
                  style={{ fontSize: maxTextSize, fontWeight: "bold" }}
                >
                  Max: {max}
                </Typography>
              </Box>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InfinityCard;
